import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AddToastProps } from '@hotjar/hotsauce';
import {
  Modal,
  Select,
  TextInput,
  CopyAction,
  Checkbox,
  tokens,
  useNotify,
} from '@hotjar/hotsauce';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { getMinutesAndSeconds } from '@hotjar/webapp/lib/getMinutesAndSeconds';
import * as Track from '@hotjar/webapp/tracking/tools/hotjar';
import { toggleShared } from '@hotjar/webapp/domains/recordings/api/recordings';
import type { Recording } from '../types';
import { trackShareModalOpen } from '../utils/tracking';

type ToggleShareProps = {
  recording: Recording;
  onSuccess: () => void;
  onError: () => void;
  showToast: (props: AddToastProps) => void;
  errorMessage: string;
};

const toggleRecordingShare = async ({
  recording,
  showToast,
  onSuccess,
  onError,
  errorMessage,
}: ToggleShareProps) => {
  Track.tagRecording('share_toggle');
  try {
    const res = await toggleShared(recording.site_id, recording.id);
    if (res.status === 200) {
      onSuccess();
    } else {
      showToast({ text: errorMessage, kind: 'danger' });
      onError();
    }
  } catch (e) {
    showToast({ text: errorMessage, kind: 'danger' });
    onError();
  }
};

type RecordingUrlProps = {
  recording: Recording;
  shared: boolean;
  startAt: boolean;
  startAtTime: number;
};
const getRecordingUrl = ({ recording, shared, startAt, startAtTime }: RecordingUrlProps) => {
  const { url, public_url: publicUrl } = recording;
  const tokenQuery = publicUrl.slice(url.length); // f.e. &token=8dd445879031b20cd43b09e940790ee5

  const withStartAt = startAt ? `&startTime=${startAtTime}` : '';
  const withShared = shared ? `${tokenQuery}` : '';

  return `${url}${withStartAt}${withShared}`;
};

const Row = styled.div`
  margin-bottom: ${tokens.spacer.x2};
`;

const parseMinutesAndSeconds = (time: string): Array<number> => {
  const splitTime = time.split(':');
  const [minutes, seconds] = splitTime;
  return [parseInt(minutes, 10) || 0, parseInt(seconds, 10) || 0];
};

const parseTime = (timeFormatted: string): number => {
  const [minutes, seconds] = parseMinutesAndSeconds(timeFormatted);
  return (minutes * 60 + seconds) * 1000;
};

function isPositiveInteger(theNumberString: string) {
  return Number.isInteger(Number(theNumberString)) && Number(theNumberString) >= 0;
}

const isValidTime = (time: string): boolean => {
  const splitTime = time.split(':');
  if (splitTime.length !== 2) {
    return false;
  }

  const [minutes, seconds] = splitTime;
  if (seconds.length > 2) {
    return false;
  }

  if (!isPositiveInteger(minutes) || !isPositiveInteger(seconds)) {
    return false;
  }

  return true;
};

type ShareModalProps = {
  isOpen: boolean;
  isShared: boolean;
  onClose: () => void;
  recording: Recording;
  startAt?: boolean;
  onShare: (value: boolean) => void;
  uiPosition: string;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
};

export const ShareModal = ({
  isOpen,
  isShared,
  onClose,
  recording,
  startAt = true,
  onShare,
  isPlaying,
  play,
  pause,
  uiPosition,
}: ShareModalProps): JSX.Element | null => {
  const { t } = useTranslation();

  const { showToast } = useNotify();

  const [time, setTime] = useState(getMinutesAndSeconds(useRealTimeStore.getState().currentTime));
  const [shared, setShared] = useState(isShared);
  const [isStartAt, setIsStartAt] = useState(startAt);
  const [wasPlaying, setWasPlaying] = useState(false);

  useEffect(() => {
    setShared(isShared);
  }, [isShared, setShared]);

  useEffect(() => {
    if (isPlaying && isOpen && pause) {
      pause();
      setWasPlaying(true);
    } else if (wasPlaying && !isOpen && play) {
      play();
      setWasPlaying(false);
    }
  }, [recording, isOpen, play, pause, isPlaying, wasPlaying]);

  useEffect(() => {
    if (isOpen) {
      setTime(getMinutesAndSeconds(useRealTimeStore.getState().currentTime));
    }
  }, [isOpen]);

  const errorMessage = t('common:share-recording.share-recording-error');

  const shareRecording = useCallback(
    (value) => {
      toggleRecordingShare({
        recording,
        showToast,
        onSuccess: () => onShare(value === 'true'),
        onError: () => onShare(value === 'true'),
        errorMessage,
      });
    },
    [showToast, recording, onShare, errorMessage],
  );

  if (!isOpen) {
    return null;
  }

  const timeInMs = parseTime(time);
  const url = getRecordingUrl({
    recording,
    shared,
    startAt: isStartAt,
    startAtTime: timeInMs,
  });

  return (
    <Modal
      contentLabel={t('common:share-recording.share-recording-title')}
      isOpen={isOpen}
      onClose={onClose}
      onAfterOpen={() => {
        trackShareModalOpen(recording, uiPosition);
      }}
      onAfterClose={() => {
        //
      }}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      size="s"
      css={css`
        width: 566px;
      `}
    >
      <Modal.Header title={t('common:share-recording.share-recording-title')} />
      <Modal.Body>
        <Row>
          <TextInput
            id="share_url"
            name="share_url"
            value={url}
            placeholder="Placeholder"
            isReadonly
            actionContent={
              <CopyAction
                onCopy={() => {
                  //
                }}
                toCopy={url}
                description="Copy url"
              />
            }
          />
        </Row>
        <Row
          css={css`
            display: flex;
          `}
        >
          <Checkbox
            id="start_at"
            name="start_at"
            value="true"
            css={css`
              flex-shrink: 0;
              text-transform: unset;
              margin: 0;
              align-items: center;
            `}
            labelContent={<span>{t('common:share-recording.start-at')}</span>}
            isChecked={isStartAt}
            onChange={() => setIsStartAt(!isStartAt)}
          />

          <TextInput
            inputStyles={css`
              text-align: center;
            `}
            css={css`
              margin-left: ${tokens.spacer.x1};
              width: 76px;
            `}
            size="s"
            id="share_time"
            name="share_time"
            isDisabled={!isStartAt}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const isValid = isValidTime(event.target.value);
              if (isValid && parseTime(event.target.value) > recording.duration) {
                setTime(getMinutesAndSeconds(recording.duration));
              } else if (isValid) {
                setTime(event.target.value);
              }
            }}
            value={time}
          />
        </Row>
        <Row
          css={css`
            display: inline-block;
          `}
        >
          <Select
            id="share_select_public"
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              shareRecording(event.target.value);
              setShared(event.target.value === 'true');
            }}
            icon={shared ? 'site' : 'lock'}
            value={shared ? 'true' : 'false'}
            size="s"
          >
            <option value="false">{t('common:share-recording.only-team')}</option>
            <option value="true">{t('common:share-recording.everyone')}</option>
          </Select>
        </Row>
      </Modal.Body>
    </Modal>
  );
};
